import React, { useEffect, useRef, useMemo } from "react";
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
    Tooltip
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Location {
    location_id: number;
    location_name: string;
    lat: string;
    lon: string;
}

interface AlertLocationMapPickerProps {
    locations: Location[];
    selected: number[];
    onChange: (ids: number[]) => void;
}

// ─── Icon factory ─────────────────────────────────────────────────────────────

const makeMarkerIcon = (selected: boolean) =>
    new L.DivIcon({
        className: "",
        html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
        <filter id="shadow" x="-30%" y="-20%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.35)" />
        </filter>
        <path
          d="M14 2C8.477 2 4 6.477 4 12c0 7 10 22 10 22s10-15 10-22c0-5.523-4.477-10-10-10z"
          fill="${selected ? "#28AAE1" : "#94a3b8"}"
          stroke="${selected ? "#0d7fad" : "#64748b"}"
          stroke-width="1.5"
          filter="url(#shadow)"
        />
        <circle cx="14" cy="12" r="4.5" fill="white" opacity="${selected ? "1" : "0.6"}" />
      </svg>
    `,
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        popupAnchor: [0, -38],
    });

// ─── Auto-fit bounds when locations load ─────────────────────────────────────

const FitBounds = ({ locations }: { locations: Location[] }) => {
    const map = useMap();
    const fitted = useRef(false);

    useEffect(() => {
        if (fitted.current || locations.length === 0) return;
        const validLocs = locations.filter(
            (l) => !isNaN(parseFloat(l.lat)) && !isNaN(parseFloat(l.lon))
        );
        if (validLocs.length === 0) return;

        if (validLocs.length === 1) {
            map.setView([parseFloat(validLocs[0].lat), parseFloat(validLocs[0].lon)], 10);
        } else {
            const bounds = L.latLngBounds(
                validLocs.map((l) => [parseFloat(l.lat), parseFloat(l.lon)])
            );
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        }
        fitted.current = true;
    }, [locations, map]);

    return null;
};

// ─── Main component ───────────────────────────────────────────────────────────

export const AlertLocationMapPicker: React.FC<AlertLocationMapPickerProps> = ({
    locations,
    selected,
    onChange,
}) => {
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const toggle = (id: number) => {
        if (selectedSet.has(id)) {
            onChange(selected.filter((x) => x !== id));
        } else {
            onChange([...selected, id]);
        }
    };

    const selectAll = () => onChange(locations.map((l) => l.location_id));
    const clearAll = () => onChange([]);

    const validLocations = locations.filter(
        (l) => !isNaN(parseFloat(l.lat)) && !isNaN(parseFloat(l.lon))
    );

    return (
        <div className="lmp-root">
            {/* Header row */}
            <div className="lmp-header">
                <label className="ls-label lmp-label">
                    Locations *
                    {selected.length > 0 && (
                        <span className="lmp-count-badge">{selected.length} selected</span>
                    )}
                </label>
                <div className="lmp-actions">
                    <button
                        type="button"
                        className="lmp-text-btn"
                        onClick={selectAll}
                        disabled={selected.length === locations.length}
                    >
                        Select all
                    </button>
                    <span className="lmp-divider">·</span>
                    <button
                        type="button"
                        className="lmp-text-btn"
                        onClick={clearAll}
                        disabled={selected.length === 0}
                    >
                        Clear
                    </button>
                </div>
            </div>

            {/* Map + list split */}
            <div className="lmp-body">
                {/* ── Map ── */}
                <div className="lmp-map-wrap">
                    {validLocations.length === 0 ? (
                        <div className="lmp-map-placeholder">
                            <span>Loading map…</span>
                        </div>
                    ) : (
                        <MapContainer
                            key="alert-location-map"
                            center={[39.5, -98.35]}
                            zoom={4}
                            zoomControl={false}
                            zoomAnimation={false}
                            style={{ width: "100%", height: "100%" }}
                        >
                            <TileLayer
                                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            />
                            <FitBounds locations={validLocations} />

                            {validLocations.map((loc) => {
                                const isSelected = selectedSet.has(loc.location_id);
                                return (
                                    <Marker
                                        key={loc.location_id}
                                        position={[parseFloat(loc.lat), parseFloat(loc.lon)]}
                                        icon={makeMarkerIcon(isSelected)}
                                        eventHandlers={{
                                            click: () => toggle(loc.location_id),
                                        }}
                                        zIndexOffset={isSelected ? 100 : 0}
                                    >
                                        <Tooltip
                                            permanent
                                            direction="top"
                                            offset={[0, -38]}
                                            className="sm-tooltip"
                                        >
                                            {loc.location_name}
                                        </Tooltip>

                                        <Popup>
                                            <div className="lmp-popup">
                                                <strong>{loc.location_name}</strong>
                                                <button
                                                    type="button"
                                                    className={`lmp-popup-btn ${isSelected ? "selected" : ""}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggle(loc.location_id);
                                                    }}
                                                >
                                                    {isSelected ? "✓ Selected" : "+ Select"}
                                                </button>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MapContainer>
                    )}
                </div>

                {/* ── List ── */}
                <div className="lmp-list">
                    {locations.length === 0 ? (
                        <p className="ls-locations-loading">Loading locations…</p>
                    ) : (
                        locations.map((loc) => {
                            const isSelected = selectedSet.has(loc.location_id);
                            return (
                                <button
                                    key={loc.location_id}
                                    type="button"
                                    className={`lmp-list-item ${isSelected ? "lmp-list-item-selected" : ""}`}
                                    onClick={() => toggle(loc.location_id)}
                                >
                                    {/* Checkbox visual */}
                                    <span className={`lmp-list-check ${isSelected ? "lmp-checked" : ""}`}>
                                        {isSelected && (
                                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                                <path
                                                    d="M2 5L4 7L8 3"
                                                    stroke="white"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </span>
                                    <span className="lmp-list-name">{loc.location_name}</span>
                                    {/* Pin dot */}
                                    <span
                                        className="lmp-list-dot"
                                        style={{
                                            background: isSelected ? "#28AAE1" : "#94a3b8",
                                        }}
                                    />
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default AlertLocationMapPicker;